import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { db } from '../firebase/config';
import { useAuth } from '../contexts/AuthContext';
import {
  collection, getDocs, addDoc, doc, getDoc, runTransaction,
  query, orderBy,
} from 'firebase/firestore';
import {
  Box, Container, Typography, Paper, Button, TextField, IconButton,
  Chip, CircularProgress, InputAdornment, Dialog, DialogTitle,
  DialogContent, DialogActions, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, MenuItem, Divider, Tooltip,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import SearchIcon from '@mui/icons-material/Search';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import ShareIcon from '@mui/icons-material/Share';
import EditIcon from '@mui/icons-material/Edit';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CheckIcon from '@mui/icons-material/Check';
import EmailIcon from '@mui/icons-material/Email';
import LagbasLogo from '../components/LagbasLogo';
import { buildOrderConfirmationPdf } from '../utils/orderConfirmationPdf';

const STATUSES = [
  { key: 'draft',    label: 'Utkast',    color: '#546E7A', bg: '#ECEFF1' },
  { key: 'sent',     label: 'Skickad',   color: '#1565C0', bg: '#E3F2FD' },
  { key: 'signed',   label: 'Signerad',  color: '#2E7D32', bg: '#E8F5E9' },
  { key: 'cancelled',label: 'Makulerad', color: '#B71C1C', bg: '#FFEBEE' },
];

function statusInfo(key) {
  return STATUSES.find(s => s.key === key) || STATUSES[0];
}

function fmtDate(d) {
  if (!d) return '–';
  if (typeof d === 'string') return d;
  if (d?.toDate) return d.toDate().toLocaleDateString('sv-SE');
  return '–';
}

async function generateConfirmationNumber() {
  const year = new Date().getFullYear();
  const yy = String(year).slice(-2);
  const counterRef = doc(db, 'counters', `orderConfirmations_${year}`);
  const newCount = await runTransaction(db, async tx => {
    const snap = await tx.get(counterRef);
    const current = snap.exists() ? snap.data().count : 0;
    const next = current + 1;
    tx.set(counterRef, { count: next, year });
    return next;
  });
  return `OB-${yy}-${String(newCount).padStart(3, '0')}`;
}

export default function OrderConfirmations() {
  const navigate = useNavigate();
  const { currentUser, userProfile } = useAuth();

  const [confirmations, setConfirmations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [createOpen, setCreateOpen] = useState(false);
  const [projects, setProjects] = useState([]);
  const [selectedProjectId, setSelectedProjectId] = useState('');
  const [creating, setCreating] = useState(false);
  const [shareItem, setShareItem] = useState(null);
  const [copied, setCopied] = useState(false);
  const [pdfLoading, setPdfLoading] = useState(null);

  useEffect(() => { load(); }, []); // eslint-disable-line

  async function load() {
    setLoading(true);
    const [confSnap, projSnap] = await Promise.all([
      getDocs(query(collection(db, 'orderConfirmations'), orderBy('createdAt', 'desc'))),
      getDocs(query(collection(db, 'projects'), orderBy('name'))),
    ]);
    setConfirmations(confSnap.docs.map(d => ({ id: d.id, ...d.data() })));
    setProjects(projSnap.docs.map(d => ({ id: d.id, ...d.data() })));
    setLoading(false);
  }

  async function handleCreate() {
    if (!selectedProjectId) return;
    setCreating(true);
    try {
      const project = projects.find(p => p.id === selectedProjectId);
      const confirmationNumber = await generateConfirmationNumber();
      const ref = await addDoc(collection(db, 'orderConfirmations'), {
        confirmationNumber,
        status: 'draft',
        projectId: selectedProjectId,
        projectName: project?.name || '',
        projectNumber: project?.projectNumber || '',
        clientId: project?.clientId || '',
        offerId: project?.offerId || '',
        createdAt: new Date(),
        createdBy: currentUser.uid,
        updatedAt: new Date(),
      });
      navigate(`/orderbekraftelser/${ref.id}`);
    } catch (err) {
      console.error(err);
      setCreating(false);
    }
  }

  async function handleDownloadPdf(conf, e) {
    e.stopPropagation();
    setPdfLoading(conf.id);
    try {
      let logoUrl = null;
      const companyId = userProfile?.companyId;
      if (companyId) {
        const compSnap = await getDoc(doc(db, 'companies', companyId));
        logoUrl = compSnap.data()?.logo || null;
      }
      await buildOrderConfirmationPdf({ ...conf, _logoUrl: logoUrl });
    } catch (err) {
      console.error(err);
    }
    setPdfLoading(null);
  }

  function handleCopyLink(conf) {
    const url = `${window.location.origin}/orderbekraftelser/${conf.id}`;
    navigator.clipboard.writeText(url);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  }

  const filtered = confirmations.filter(c => {
    if (!search) return true;
    const q = search.toLowerCase();
    return (
      (c.confirmationNumber || '').toLowerCase().includes(q) ||
      (c.projectName || '').toLowerCase().includes(q) ||
      (c.ourProjectName || '').toLowerCase().includes(q) ||
      (c.clientName || '').toLowerCase().includes(q)
    );
  });

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#f0f2f5' }}>
      <Box sx={{ bgcolor: '#1a1a1a', color: 'white', py: 1.5, px: 3,
        display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <LagbasLogo />
          <Divider orientation="vertical" flexItem sx={{ borderColor: 'rgba(255,255,255,0.2)', mx: -0.5 }} />
          <IconButton color="inherit" size="small" onClick={() => navigate(-1)}>
            <ArrowBackIcon />
          </IconButton>
          <Box>
            <Typography variant="subtitle1" fontWeight="bold">Orderbekräftelser</Typography>
            <Typography variant="caption" sx={{ color: '#aaa' }}>
              Bekräftade konsultuppdrag — PDF och delning
            </Typography>
          </Box>
        </Box>
        <Button variant="contained" startIcon={<AddIcon />}
          onClick={() => { setSelectedProjectId(''); setCreateOpen(true); }}
          sx={{ bgcolor: '#FF6B00', '&:hover': { bgcolor: '#e55a00' }, textTransform: 'none' }}>
          Ny orderbekräftelse
        </Button>
      </Box>

      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Box sx={{ mb: 3 }}>
          <TextField size="small" placeholder="Sök nummer, projekt eller kund…"
            value={search} onChange={e => setSearch(e.target.value)}
            InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon sx={{ color: '#aaa' }} /></InputAdornment> }}
            sx={{ width: 380 }} />
        </Box>

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
            <CircularProgress />
          </Box>
        ) : filtered.length === 0 ? (
          <Paper sx={{ p: 6, textAlign: 'center' }}>
            <Typography color="text.secondary">
              {search ? 'Inga bekräftelser matchar sökningen.' : 'Inga orderbekräftelser ännu — skapa den första!'}
            </Typography>
          </Paper>
        ) : (
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                  <TableCell sx={{ fontWeight: 700 }}>Nummer</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Projekt</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Kund</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Datum</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Status</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700 }}>Åtgärder</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filtered.map(conf => {
                  const st = statusInfo(conf.status);
                  return (
                    <TableRow key={conf.id} hover sx={{ cursor: 'pointer' }}
                      onClick={() => navigate(`/orderbekraftelser/${conf.id}`)}>
                      <TableCell>
                        <Typography variant="body2" fontWeight={700} sx={{ fontFamily: 'monospace', color: '#FF6B00' }}>
                          {conf.confirmationNumber || '–'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">{conf.ourProjectName || conf.projectName || '–'}</Typography>
                        {conf.offerNumber && (
                          <Typography variant="caption" color="text.secondary">Offert: {conf.offerNumber}</Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">{conf.clientName || '–'}</Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">{conf.signingDate || fmtDate(conf.createdAt)}</Typography>
                      </TableCell>
                      <TableCell>
                        <Chip label={st.label} size="small"
                          sx={{ bgcolor: st.bg, color: st.color, fontWeight: 600, fontSize: '0.7rem' }} />
                      </TableCell>
                      <TableCell align="right" onClick={e => e.stopPropagation()}>
                        <Tooltip title="Ladda ned PDF">
                          <IconButton size="small" disabled={pdfLoading === conf.id}
                            onClick={e => handleDownloadPdf(conf, e)}>
                            <PictureAsPdfIcon fontSize="small"
                              sx={{ color: pdfLoading === conf.id ? '#ccc' : '#B71C1C' }} />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Dela">
                          <IconButton size="small" onClick={e => { e.stopPropagation(); setShareItem(conf); setCopied(false); }}>
                            <ShareIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Redigera">
                          <IconButton size="small" onClick={() => navigate(`/orderbekraftelser/${conf.id}`)}>
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Container>

      {/* Create dialog */}
      <Dialog open={createOpen} onClose={() => !creating && setCreateOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Ny orderbekräftelse</DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: '12px !important' }}>
          <TextField select label="Koppla till uppdrag (UppdragN)" size="small" fullWidth
            value={selectedProjectId} onChange={e => setSelectedProjectId(e.target.value)}>
            <MenuItem value="">– Välj uppdrag –</MenuItem>
            {projects.map(p => (
              <MenuItem key={p.id} value={p.id}>
                {p.name}{p.projectNumber ? ` (${p.projectNumber})` : ''}
              </MenuItem>
            ))}
          </TextField>
          <Typography variant="caption" color="text.secondary">
            Kund, offertnummer och budget hämtas automatiskt från uppdraget och dess kopplade offert.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setCreateOpen(false)} disabled={creating}>Avbryt</Button>
          <Button variant="contained" disabled={!selectedProjectId || creating}
            onClick={handleCreate}
            sx={{ bgcolor: '#FF6B00', '&:hover': { bgcolor: '#e55a00' }, textTransform: 'none' }}>
            {creating ? 'Skapar…' : 'Skapa och öppna'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Share dialog */}
      <Dialog open={!!shareItem} onClose={() => setShareItem(null)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <ShareIcon sx={{ color: '#FF6B00' }} />
          Dela orderbekräftelse
        </DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: '12px !important' }}>
          {shareItem && (
            <>
              <Typography variant="body2" fontWeight={600} sx={{ color: '#FF6B00' }}>
                {shareItem.confirmationNumber} – {shareItem.ourProjectName || shareItem.projectName}
              </Typography>
              <TextField size="small" fullWidth label="Intern länk (kräver inloggning)"
                value={`${window.location.origin}/orderbekraftelser/${shareItem.id}`}
                InputProps={{ readOnly: true }} />
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Button variant="contained" startIcon={copied ? <CheckIcon /> : <ContentCopyIcon />}
                  onClick={() => handleCopyLink(shareItem)}
                  sx={{ bgcolor: copied ? '#2E7D32' : '#1a1a1a', '&:hover': { bgcolor: copied ? '#1b5e20' : '#333' }, textTransform: 'none' }}>
                  {copied ? 'Kopierad!' : 'Kopiera länk'}
                </Button>
                {shareItem.clientContactEmail && (
                  <Button variant="outlined" startIcon={<EmailIcon />}
                    href={`mailto:${shareItem.clientContactEmail}?subject=Orderbekräftelse ${shareItem.confirmationNumber}&body=Hej,\n\nBifogar orderbekräftelse för ${shareItem.ourProjectName || shareItem.projectName || 'uppdraget'}.\n\nMed vänliga hälsningar`}
                    sx={{ textTransform: 'none' }}>
                    Öppna i mail
                  </Button>
                )}
              </Box>
            </>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setShareItem(null)}>Stäng</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
